import { jsPDF } from "jspdf";
import type { HiringManagerResult, Contact } from "../hooks/useHiringManagerSearch";

const PAGE_W  = 210;
const PAGE_H  = 297;
const MARGIN  = 18;
const BODY_W  = PAGE_W - MARGIN * 2;

function addWrappedText(
  doc: jsPDF,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
): number {
  const lines = doc.splitTextToSize(text, maxWidth);
  doc.text(lines, x, y);
  return y + lines.length * lineHeight;
}

export function exportHiringManagerPdf(result: HiringManagerResult) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const contacts: Contact[] = result.contacts ?? [];

  let y = MARGIN;

  // ── Header bar ───────────────────────────────────────────────────────────────
  doc.setFillColor(26, 26, 46);
  doc.rect(0, 0, PAGE_W, 28, "F");

  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.text("HiringManagerID", MARGIN, 12);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(180, 180, 200);
  doc.text("Powered by TailorWiz & JobIntel 360", MARGIN, 18);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(253, 224, 71);
  doc.text(result.company ?? "Hiring Manager Report", MARGIN, 24);

  const meta = [result.job_title, result.location].filter(Boolean).join("  ·  ");
  if (meta) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(200, 200, 220);
    doc.text(meta, MARGIN + doc.getTextWidth(result.company ?? "") + 4, 24);
  }

  const dateStr = new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(160, 160, 180);
  doc.text(dateStr, PAGE_W - MARGIN, 24, { align: "right" });

  y = 36;

  // ── Section label ─────────────────────────────────────────────────────────────
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  doc.setTextColor(140, 140, 160);
  doc.text(`DECISION MAKERS & CONTACTS (${contacts.length})`, MARGIN, y);
  doc.setDrawColor(220, 220, 230);
  doc.setLineWidth(0.2);
  doc.line(MARGIN, y + 1.5, PAGE_W - MARGIN, y + 1.5);
  y += 7;

  // ── Contacts ──────────────────────────────────────────────────────────────────
  contacts.forEach((c: Contact, i: number) => {
    const LH_BODY  = 4.5;
    const LH_SMALL = 4;

    const summaryText = c.profile_summary ?? c.summary ?? "";
    const tipText     = c.outreach_tip ?? "";
    const summaryLines = summaryText ? doc.setFontSize(8.5) && doc.splitTextToSize(summaryText, BODY_W - 10).length : 0;
    const tipLines     = tipText     ? doc.setFontSize(8)   && doc.splitTextToSize(tipText,     BODY_W - 18).length : 0;
    const estimatedH   = 6 + (c.linkedin_url ? 5 : 0) + summaryLines * LH_BODY + (summaryLines ? 2 : 0) + tipLines * LH_SMALL + (tipLines ? 7 : 0) + 5;

    if (y + estimatedH > PAGE_H - MARGIN) {
      doc.addPage();
      y = MARGIN;
    }

    // Rank badge
    doc.setFillColor(26, 26, 46);
    doc.circle(MARGIN + 3, y + 2, 3, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(6.5);
    doc.setTextColor(255, 255, 255);
    doc.text(String(c.rank ?? i + 1), MARGIN + 3, y + 2.8, { align: "center" });

    // Name + title
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(26, 26, 46);
    doc.text(c.full_name ?? "", MARGIN + 8, y + 3);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(80, 80, 100);
    doc.text(c.title ?? "", MARGIN + 8, y + 7.5);

    y += 11;

    // LinkedIn URL — full text
    if (c.linkedin_url) {
      doc.setFont("courier", "normal");
      doc.setFontSize(7.5);
      doc.setTextColor(29, 78, 216);
      doc.text(c.linkedin_url, MARGIN + 8, y);
      y += 5;
    }

    // Summary
    if (summaryText) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(60, 60, 80);
      y = addWrappedText(doc, summaryText, MARGIN + 8, y, BODY_W - 10, LH_BODY);
      y += 2;
    }

    // Outreach tip
    if (tipText) {
      doc.setFillColor(255, 251, 235);
      const tipLines2 = doc.setFontSize(8) && doc.splitTextToSize(tipText, BODY_W - 18);
      const tipBoxH   = tipLines2.length * LH_SMALL + 5;
      doc.roundedRect(MARGIN + 8, y, BODY_W - 10, tipBoxH, 1.5, 1.5, "F");
      doc.setDrawColor(245, 158, 11);
      doc.setLineWidth(0.5);
      doc.line(MARGIN + 8, y, MARGIN + 8, y + tipBoxH);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7.5);
      doc.setTextColor(146, 64, 14);
      doc.text("Outreach Tip:", MARGIN + 11, y + 3.5);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.text(tipLines2, MARGIN + 11, y + 7);
      y += tipBoxH + 3;
    }

    if (i < contacts.length - 1) {
      doc.setDrawColor(235, 235, 240);
      doc.setLineWidth(0.15);
      doc.line(MARGIN, y + 1, PAGE_W - MARGIN, y + 1);
      y += 5;
    }
  });

  // ── Footer ────────────────────────────────────────────────────────────────────
  const totalPages = (doc as unknown as { internal: { getNumberOfPages: () => number } }).internal.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(160, 160, 180);
    doc.text("HiringManagerID — tailorwiz.com — Confidential", MARGIN, PAGE_H - 8);
    doc.text(`Page ${p} of ${totalPages}`, PAGE_W - MARGIN, PAGE_H - 8, { align: "right" });
  }

  const filename = `HiringManager_${(result.company ?? "Report").replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(filename);
}
